/**
 * Types and functions for 2d planar graphs that can be used to lookup adjacencies between arbitrary polygons
 * @module
 */
import { DIST_EPSILON_SQUARED, WindingDir } from "./core";
import { poleOfInaccessibilityPolyLine2 } from "./label";
import { line2Eval, line2Normal, line2Tangent } from "./line2";
import { polyLine2Simplify } from "./polyLine2";
import { polyLine2New, type PolyLine2 } from "./polyLine2Types";
import { quicksort } from "./quicksort";
import { hashInts2 } from "./utils";
import type { Vec2 } from "./vec2";
import { vec2Dist, vec2DistSquared, vec2Dot, vec2Result } from "./vec2";

const { sqrt, atan2 } = Math;

export interface PlanarGraphLoops {
  readonly faceVertices: number[][];
  readonly faceHalfEdges: number[][];
}

export interface PlanarGraphFacePoleOfInaccessibilityPositions {
  readonly facePositions: Vec2[]; // locations of output from poleOfInaccessibilityPolygon for each face
}

interface PlanarGraphFaces {
  faceWindingDirs: WindingDir[];
  faceVertices: number[][]; // the indices of the vertices of the loop bounding this face
  faceHalfEdges: number[][]; // the half edge indices of the loop bounding this face
  halfEdgeParentFace: number[]; // maps a half edge to its parent face
}

interface PlanarGraphVertices {
  vertexPositions: ArrayLike<number>; // 2d vertex positions
  halfEdges: number[]; // the list of halfEdges - basically directed vertex pairs
  vertexHalfEdges: number[][]; // the indices of the half edges emanating from this vertex in the halfEdges array, sorted by CCW angle around the vertex
  halfEdgeVertexIndex: number[]; // maps half edge index to the index of the half edge in the vertexHalfEdge array from which this half edge emanates
}

export type PlanarGraph = PlanarGraphFaces & PlanarGraphVertices;

export function planarGraphByVerticesEdges(verts: ArrayLike<number>, edges: ArrayLike<number>): PlanarGraph {
  const vertices = planarGraphVertices(verts, edges);
  const faces = buildPlanarGraphFaces(vertices);

  return { ...vertices, ...faces };
}

export function planarGraphVertexLoopWindingDir(
  { vertexPositions }: Pick<PlanarGraph, "vertexPositions">,
  vertexIndices: ArrayLike<number>,
): WindingDir {
  let sum = 0;

  for (let i = 0, l = vertexIndices.length; i < l; i++) {
    const v0 = vertexIndices[i];
    const v1 = vertexIndices[(i + 1) % l];

    const x0 = vertexPositions[2 * v0];
    const y0 = vertexPositions[2 * v0 + 1];

    const x1 = vertexPositions[2 * v1];
    const y1 = vertexPositions[2 * v1 + 1];

    sum += (x1 - x0) * (y0 + y1);
  }

  return sum > 0 ? WindingDir.CW : WindingDir.CCW;
}

function init2DNumArray(l: number): number[][] {
  const a = new Array<Array<number>>(l);
  for (let i = 0; i < l; i++) {
    a[i] = [];
  }
  return a;
}

export function planarGraphHalfEdgeStripToPolyLine2(
  planarGraph: PlanarGraph,
  halfEdgeLoop: ArrayLike<number>,
): PolyLine2 {
  const plen = halfEdgeLoop.length * 2 + 2;
  const pl = polyLine2New(plen);

  for (let i = 0, l = halfEdgeLoop.length; i < l; i++) {
    const [x, y] = planarGraphHalfEdgeStart(planarGraph, halfEdgeLoop[i]);

    const i2 = 2 * i;
    pl[i2] = x;
    pl[i2 + 1] = y;
  }

  const [x, y] = planarGraphHalfEdgeEnd(planarGraph, halfEdgeLoop[halfEdgeLoop.length - 1]);
  pl[plen - 2] = x;
  pl[plen - 1] = y;

  return pl;
}

export function planarGraphHalfEdgeStripBreakByPredicate(
  planarGraph: PlanarGraph,
  halfEdgeLoop: number[],
  predicate: (halfEdge: number, index: number) => boolean,
): number[][] {
  planarGraph;
  const strips: number[][] = [];

  let curStrip: number[] | null = null;
  for (let i = 0, l = halfEdgeLoop.length; i < l; i++) {
    const halfEdge = halfEdgeLoop[i];
    const predicateVal = predicate(halfEdge, i);
    if (predicateVal) {
      // the strip may not be initialized yet
      if (!curStrip) {
        curStrip = [];
      }

      curStrip.push(halfEdge);

      // if we are at the end of the loop,
      // we terminate the strip
      if (i === l - 1) {
        strips.push(curStrip);
      }
    }

    if (!predicateVal && curStrip && curStrip.length > 0) {
      // we have a non-empty strip, but the predicate is no longer
      // true. add the current strip to the result list.
      strips.push(curStrip);

      // reset the current strip
      curStrip = null;
    }
  }

  //if the last and first strip are connected, turn them into a single strip
  if (strips.length > 1) {
    const first = strips[0];
    const last = strips[strips.length - 1];
    const [firstX, firstY] = planarGraphHalfEdgeStart(planarGraph, first[0]);
    const [lastX, lastY] = planarGraphHalfEdgeEnd(planarGraph, last[last.length - 1]);

    if (vec2DistSquared(firstX, firstY, lastX, lastY) < DIST_EPSILON_SQUARED) {
      const firstStrip = strips.shift() ?? [];
      const lastStrip = strips.pop() ?? [];
      lastStrip.push(...firstStrip);
      strips.push(lastStrip);
    }
  }

  return strips;
}

export function planarGraphUnionFaces(planarGraph: PlanarGraph, faceIndices: number[]): PolyLine2[] {
  const loops = planarGraphTraceFaceGroupPerimeter(planarGraph, faceIndices);

  const pls: PolyLine2[] = [];
  for (const vertexLoop of loops.faceVertices) {
    pls.push(planarGraphVertexStripToPolyLine2(planarGraph, vertexLoop));
  }

  return pls;
}

export function planarGraphVertexStripToPolyLine2(
  { vertexPositions }: PlanarGraph,
  vertexIndices: ArrayLike<number>,
  simplify: boolean = false,
): PolyLine2 {
  const plen = vertexIndices.length * 2 + 2;
  const pl = polyLine2New(plen);

  for (let i = 0, l = vertexIndices.length; i < l; i++) {
    const vi = vertexIndices[i];
    const i2 = 2 * i;
    pl[i2] = vertexPositions[2 * vi];
    pl[i2 + 1] = vertexPositions[2 * vi + 1];
  }

  const f0 = 2 * vertexIndices[0];
  pl[plen - 2] = vertexPositions[f0];
  pl[plen - 1] = vertexPositions[f0 + 1];

  return simplify ? polyLine2Simplify(pl) : pl;
}

export function planarGraphHalfEdgeStripArea(graph: PlanarGraph, halfEdges: number[]): number {
  return Math.abs(planarGraphHalfEdgeStripSignedArea(graph, halfEdges));
}

export function planarGraphHalfEdgeStripSignedArea(graph: PlanarGraph, halfEdges: number[]): number {
  let sum = 0;

  for (let i = 0, l = halfEdges.length; i < l; i++) {
    const [x0, y0] = planarGraphHalfEdgeStart(graph, halfEdges[i]);
    const [x1, y1] = planarGraphHalfEdgeEnd(graph, halfEdges[i]);

    sum += (x1 - x0) * (y0 + y1);
  }

  return sum / 2;
}

export function planarGraphBoundingFaceIndices(planarGraph: PlanarGraph): number[] {
  const faces: number[] = [];
  const faceVertices = planarGraph.faceVertices;
  for (let i = 0, l = planarGraph.faceVertices.length; i < l; i++) {
    if (planarGraphVertexLoopWindingDir(planarGraph, faceVertices[i]) === WindingDir.CW) {
      faces.push(i);
    }
  }
  return faces;
}

export function planarGraphHalfEdgesCollinear(
  planarGraph: PlanarGraph,
  halfEdgeIndexTest: number,
  halfEdgeIndex: number,
) {
  // same direction
  const [cwDirX, cwDirY] = planarGraphHalfEdgeTangent(planarGraph, halfEdgeIndexTest);
  const [dirX, dirY] = planarGraphHalfEdgeTangent(planarGraph, halfEdgeIndex);

  if (Math.abs(vec2Dot(cwDirX, cwDirY, dirX, dirY) - 1) < 1e-3) {
    return true;
  }

  // not compatible
  return false;
}

export function planarGraphNextCollinearHalfEdgeInLoop(
  planarGraph: PlanarGraph,
  halfEdgeIndex: number,
  halfEdgeLoop: number[],
): number {
  const he = planarGraphNextHalfEdgeInLoop(halfEdgeLoop, halfEdgeIndex);

  if (he === -1) {
    return -1;
  }

  return planarGraphHalfEdgesCollinear(planarGraph, he, halfEdgeIndex) ? he : -1;
}

export function planarGraphPrevCollinearHalfEdgeInLoop(
  planarGraph: PlanarGraph,
  halfEdgeIndex: number,
  halfEdgeLoop: number[],
): number {
  const he = planarGraphPrevHalfEdgeInLoop(halfEdgeLoop, halfEdgeIndex);

  if (he === -1) {
    return -1;
  }
  return planarGraphHalfEdgesCollinear(planarGraph, he, halfEdgeIndex) ? he : -1;
}

export function planarGraphNextHalfEdgeOnCollinearStrip(planarGraph: PlanarGraph, halfEdgeIndex: number): number {
  const adjHalfEdgeIndex = planarGraphAdjacentHalfEdge(halfEdgeIndex);

  const he = planarGraphNextHalfEdgeCCWAroundStartVertex(planarGraph, adjHalfEdgeIndex, (he) => {
    return planarGraphHalfEdgesCollinear(planarGraph, he, halfEdgeIndex);
  });

  return he;
}

export function planarGraphPrevHalfEdgeOnCollinearStrip(planarGraph: PlanarGraph, halfEdgeIndex: number): number {
  const he = planarGraphNextHalfEdgeCCWAroundStartVertex(planarGraph, halfEdgeIndex, (he) => {
    return planarGraphHalfEdgesCollinear(planarGraph, planarGraphAdjacentHalfEdge(he), halfEdgeIndex);
  });

  if (he !== -1) {
    return planarGraphAdjacentHalfEdge(he);
  }

  return -1;
}

export function planarGraphNextHalfEdgeCCWAroundStartVertex(
  planarGraph: PlanarGraph,
  halfEdgeIndex: number,
  pred?: (halfEdgeIndex: number) => boolean,
): number {
  const startVertex = planarGraph.halfEdges[halfEdgeIndex * 2];

  const outgoingHalfEdges = planarGraph.vertexHalfEdges[startVertex];
  const outgoingHalfEdgeCount = outgoingHalfEdges.length;

  const halfEdgeIndexInVertexHalfEdgeArray = planarGraph.halfEdgeVertexIndex[halfEdgeIndex];

  for (let i = 1; i < outgoingHalfEdgeCount; i++) {
    const nxt = outgoingHalfEdges[(halfEdgeIndexInVertexHalfEdgeArray + i) % outgoingHalfEdgeCount];
    if (!pred || pred(nxt)) {
      return nxt;
    }
  }

  return -1;
}

export function planarGraphAdjacentHalfEdge(halfEdgeIndex: number): number {
  return halfEdgeIndex % 2 === 0 ? halfEdgeIndex + 1 : halfEdgeIndex - 1;
}

function planarGraphVertices(vertexPositions: ArrayLike<number>, edges: ArrayLike<number>): PlanarGraphVertices {
  // build half edges as pairs - this structure is useful for fast lookup of the opp half edge
  const halfEdges = new Array<number>(edges.length * 2); // like (a,b), (b,a), (b,c), (c,b), ...

  for (let i = 0, l = edges.length; i < l; i += 2) {
    const i2 = 2 * i;

    halfEdges[i2] = edges[i];
    halfEdges[i2 + 1] = edges[i + 1];
    halfEdges[i2 + 2] = edges[i + 1];
    halfEdges[i2 + 3] = edges[i];
  }

  const numVerts = vertexPositions.length / 2;
  const numHalfEdges2 = halfEdges.length;

  const vertexHalfEdges = init2DNumArray(numVerts);

  for (let i = 0; i < numHalfEdges2; i += 2) {
    const start = halfEdges[i];
    vertexHalfEdges[start].push(i / 2);
  }

  // for each half edge, compute angle around vertex

  const vertexAngles = init2DNumArray(numVerts);

  for (let i = 0; i < numVerts; i++) {
    const x = vertexPositions[2 * i];
    const y = vertexPositions[2 * i + 1];

    const localHalfEdges = vertexHalfEdges[i];
    for (let j = 0, jl = localHalfEdges.length; j < jl; j++) {
      const end = halfEdges[2 * localHalfEdges[j] + 1];

      const xa = vertexPositions[2 * end] - x;
      const ya = vertexPositions[2 * end + 1] - y;

      const al = sqrt(xa * xa + ya * ya);

      vertexAngles[i].push(atan2(ya / al, xa / al));
    }
  }

  let curVertexAngles: number[];
  let curVertexHalfEdges: number[];

  function compare(a: number, b: number) {
    return curVertexAngles[a] > curVertexAngles[b];
  }

  function swap(a: number, b: number) {
    // swap halfedge angles
    const aang = curVertexAngles[a];
    const bang = curVertexAngles[b];

    curVertexAngles[b] = aang;
    curVertexAngles[a] = bang;

    // swap halfedges
    const ah = curVertexHalfEdges[a];
    const bh = curVertexHalfEdges[b];

    curVertexHalfEdges[b] = ah;
    curVertexHalfEdges[a] = bh;
  }

  // for each vertex, sort the edges by ccw angle
  for (let i = 0; i < numVerts; i++) {
    // update the sort func closure vars
    curVertexAngles = vertexAngles[i];
    curVertexHalfEdges = vertexHalfEdges[i];

    // sort
    quicksort(0, curVertexHalfEdges.length - 1, compare, swap);
  }

  // for each half edge, store its index in its vertexHalfEdges array, this is an optimization
  const halfEdgeVertexIndex = new Array(halfEdges.length);
  for (const halfEdgeSet of vertexHalfEdges) {
    for (let i = 0, l = halfEdgeSet.length; i < l; i++) {
      halfEdgeVertexIndex[halfEdgeSet[i]] = i;
    }
  }

  return {
    halfEdges,
    vertexPositions,
    vertexHalfEdges,
    halfEdgeVertexIndex,
  };
}

export function planarGraphHalfEdgeLine2ByFaceEdge(
  { faceHalfEdges, vertexPositions, halfEdges }: PlanarGraph,
  faceIndex: number,
  edgeIndex: number,
): PolyLine2 {
  const halfEdgeIndex = faceHalfEdges[faceIndex][edgeIndex];

  const v0 = halfEdges[halfEdgeIndex * 2];
  const v1 = halfEdges[halfEdgeIndex * 2 + 1];

  return polyLine2New([
    vertexPositions[v0 * 2],
    vertexPositions[v0 * 2 + 1],

    vertexPositions[v1 * 2],
    vertexPositions[v1 * 2 + 1],
  ]);
}

export function planarGraphHalfEdgeLength({ vertexPositions, halfEdges }: PlanarGraph, halfEdgeIndex: number): number {
  const v0 = halfEdges[halfEdgeIndex * 2];
  const v1 = halfEdges[halfEdgeIndex * 2 + 1];

  return vec2Dist(
    vertexPositions[v0 * 2],
    vertexPositions[v0 * 2 + 1],

    vertexPositions[v1 * 2],
    vertexPositions[v1 * 2 + 1],
  );
}

export function planarGraphHalfEdgeStart({ vertexPositions, halfEdges }: PlanarGraph, halfEdgeIndex: number): Vec2 {
  const v0 = halfEdges[halfEdgeIndex * 2];

  vec2Result[0] = vertexPositions[v0 * 2];
  vec2Result[1] = vertexPositions[v0 * 2 + 1];

  return vec2Result;
}

export function planarGraphHalfEdgeStartVertex({ halfEdges }: PlanarGraph, halfEdgeIndex: number): number {
  return halfEdges[halfEdgeIndex * 2];
}

export function planarGraphHalfEdgeEndVertex({ halfEdges }: PlanarGraph, halfEdgeIndex: number): number {
  return halfEdges[halfEdgeIndex * 2 + 1];
}

export function planarGraphHalfEdgeEnd({ vertexPositions, halfEdges }: PlanarGraph, halfEdgeIndex: number): Vec2 {
  const v1 = halfEdges[halfEdgeIndex * 2 + 1];

  vec2Result[0] = vertexPositions[v1 * 2];
  vec2Result[1] = vertexPositions[v1 * 2 + 1];

  return vec2Result;
}

export function planarGraphHalfEdgeTangent(
  { vertexPositions, halfEdges }: PlanarGraph,
  halfEdgeIndex: number,
  negate: boolean = false,
): Vec2 {
  const v0 = halfEdges[halfEdgeIndex * 2];
  const v1 = halfEdges[halfEdgeIndex * 2 + 1];

  return line2Tangent(
    vertexPositions[v0 * 2],
    vertexPositions[v0 * 2 + 1],

    vertexPositions[v1 * 2],
    vertexPositions[v1 * 2 + 1],

    negate,
  );
}

export function planarGraphVertex({ vertexPositions }: PlanarGraph, vertex: number): Vec2 {
  vec2Result[0] = vertexPositions[vertex * 2];
  vec2Result[1] = vertexPositions[vertex * 2 + 1];

  return vec2Result;
}

export function planarGraphHalfEdgeNormal(
  { vertexPositions, halfEdges }: PlanarGraph,
  halfEdgeIndex: number,
  negate: boolean = false,
): Vec2 {
  const v0 = halfEdges[halfEdgeIndex * 2];
  const v1 = halfEdges[halfEdgeIndex * 2 + 1];

  return line2Normal(
    vertexPositions[v0 * 2],
    vertexPositions[v0 * 2 + 1],

    vertexPositions[v1 * 2],
    vertexPositions[v1 * 2 + 1],

    negate,
  );
}

export function planarGraphHalfEdgeEval(
  { vertexPositions, halfEdges }: PlanarGraph,
  halfEdgeIndex: number,
  param: number,
  paramNormalized: boolean = false,
): Vec2 {
  const v0 = halfEdges[halfEdgeIndex * 2];
  const v1 = halfEdges[halfEdgeIndex * 2 + 1];

  return line2Eval(
    vertexPositions[v0 * 2],
    vertexPositions[v0 * 2 + 1],

    vertexPositions[v1 * 2],
    vertexPositions[v1 * 2 + 1],
    param,
    paramNormalized,
  );
}

export function planarGraphHalfEdgeToLine2(
  { vertexPositions, halfEdges }: PlanarGraph,
  halfEdgeIndex: number,
): PolyLine2 {
  const v0 = halfEdges[halfEdgeIndex * 2];
  const v1 = halfEdges[halfEdgeIndex * 2 + 1];

  return polyLine2New([
    vertexPositions[v0 * 2],
    vertexPositions[v0 * 2 + 1],

    vertexPositions[v1 * 2],
    vertexPositions[v1 * 2 + 1],
  ]);
}

export function planarGraphHalfEdgeToLine2Mut(
  { vertexPositions, halfEdges }: PlanarGraph,
  halfEdgeIndex: number,
  out: PolyLine2,
) {
  const v0 = halfEdges[halfEdgeIndex * 2];
  const v1 = halfEdges[halfEdgeIndex * 2 + 1];

  out[0] = vertexPositions[v0 * 2];
  out[1] = vertexPositions[v0 * 2 + 1];
  out[2] = vertexPositions[v1 * 2];
  out[3] = vertexPositions[v1 * 2 + 1];
}

export function planarGraphHalfEdgeLengthByFaceEdge(
  { faceHalfEdges, vertexPositions, halfEdges }: PlanarGraph,
  faceIndex: number,
  edgeIndex: number,
): number {
  const halfEdgeIndex = faceHalfEdges[faceIndex][edgeIndex];

  const v0 = halfEdges[halfEdgeIndex * 2];
  const v1 = halfEdges[halfEdgeIndex * 2 + 1];

  const ax = vertexPositions[v0 * 2];
  const ay = vertexPositions[v0 * 2 + 1];

  const bx = vertexPositions[v1 * 2];
  const by = vertexPositions[v1 * 2 + 1];

  return vec2Dist(ax, ay, bx, by);
}

export function planarGraphAdjacentFace(graph: PlanarGraph, faceIndex: number, edgeIndex: number): number {
  const halfEdgeIndex = graph.faceHalfEdges[faceIndex][edgeIndex];
  return graph.halfEdgeParentFace[planarGraphAdjacentHalfEdge(halfEdgeIndex)];
}

export function planarGraphAdjacentFaceEdge(
  graph: PlanarGraph,
  faceIndex: number,
  edgeIndex: number,
): { faceIndex: number; edgeIndex: number } {
  const { faceVertices, faceHalfEdges, halfEdgeParentFace } = graph;

  const vertex = faceVertices[faceIndex][edgeIndex];
  const halfEdge = faceHalfEdges[faceIndex][edgeIndex];
  const oppHalfEdge = planarGraphAdjacentHalfEdge(halfEdge);

  const oppFaceIndex = halfEdgeParentFace[oppHalfEdge];
  const oppv = faceVertices[oppFaceIndex];
  const oppEdgeIndex = (faceVertices[oppFaceIndex].indexOf(vertex) - 1 + oppv.length) % oppv.length;
  return { faceIndex: oppFaceIndex, edgeIndex: oppEdgeIndex };
}

export function planarGraphAdjacentFaces(graph: PlanarGraph, faceIndex: number): number[] {
  const { faceHalfEdges, halfEdgeParentFace } = graph;
  const halfEdges = faceHalfEdges[faceIndex];
  const l = halfEdges.length;

  const res = new Array<number>(l);

  for (let i = 0; i < l; i++) {
    const halfEdge = halfEdges[i];
    const oppHalfEdge = planarGraphAdjacentHalfEdge(halfEdge);
    res[i] = halfEdgeParentFace[oppHalfEdge];
  }

  return res;
}

export function buildPlanarGraphFaces({
  vertexHalfEdges,
  halfEdges,
  vertexPositions,
  halfEdgeVertexIndex,
}: PlanarGraphVertices): PlanarGraphFaces {
  const numHalfEdges = halfEdges.length / 2;

  const visited = new Array<boolean>(numHalfEdges);
  visited.fill(false);

  const faceVertices: number[][] = [];
  const faceHalfEdges: number[][] = [];
  const halfEdgeParentFace = new Array<number>(numHalfEdges);

  for (let i = 0; i < numHalfEdges; i++) {
    if (visited[i]) {
      continue;
    }

    let curHalfEdge = i;

    const curFaceVerts: number[] = [];
    const curFaceHalfEdges: number[] = [];

    // trace the boundary of the face
    while (!visited[curHalfEdge]) {
      visited[curHalfEdge] = true;

      const curVertex = halfEdges[curHalfEdge * 2];
      const nxtVertex = halfEdges[curHalfEdge * 2 + 1];

      const nxtVertexHalfEdges = vertexHalfEdges[nxtVertex];

      // find index of half edge from nxtVertex returning to curVertex
      const oppHalfEdge = planarGraphAdjacentHalfEdge(curHalfEdge);
      const localOppHalfEdge = halfEdgeVertexIndex[oppHalfEdge];

      // get the next half edge (cw) in the sorted edges emanating from this vertex
      const nxtCount = nxtVertexHalfEdges.length;
      const newHalfEdge = nxtVertexHalfEdges[(localOppHalfEdge - 1 + nxtCount) % nxtCount];

      curFaceVerts.push(curVertex);
      curFaceHalfEdges.push(curHalfEdge);

      halfEdgeParentFace[curHalfEdge] = faceVertices.length;

      curHalfEdge = newHalfEdge;
    }

    faceVertices.push(curFaceVerts);
    faceHalfEdges.push(curFaceHalfEdges);
  }

  return {
    faceVertices,
    faceHalfEdges,
    halfEdgeParentFace,
    faceWindingDirs: faceVertices.map((loop) => planarGraphVertexLoopWindingDir({ vertexPositions }, loop)),
  };
}

export function planarGraphLoopsReverse(loops: PlanarGraphLoops): PlanarGraphLoops {
  return {
    faceHalfEdges: loops.faceHalfEdges.map((x) => x.map(planarGraphAdjacentHalfEdge).reverse()),
    faceVertices: loops.faceVertices.map((x) => x.reverse()),
  };
}

export function planarGraphTraceFaceGroupPerimeter(graph: PlanarGraph, faceIndices: number[]): PlanarGraphLoops {
  const excludedFaces: number[] = [];

  for (let i = 0, l = graph.faceHalfEdges.length; i < l; i++) {
    if (faceIndices.indexOf(i) >= 0) {
      continue;
    }
    excludedFaces.push(i);
  }

  return planarGraphTracePerimeter(graph, excludedFaces);
}

// planarGraphTracePerimeter produces a perimeter edge collection, optionally excluding some faces from the tracing
// process.
export function planarGraphTracePerimeter(graph: PlanarGraph, excludedFaceIndices?: number[]): PlanarGraphLoops {
  // we'll store the result in these data structures
  const faceVertices: number[][] = [];
  const faceHalfEdges: number[][] = [];

  const boundFaceIndices = planarGraphBoundingFaceIndices(graph);

  // shortcut for empty excluded faces
  if (!excludedFaceIndices || excludedFaceIndices.length === 0) {
    for (let i = 0, l = boundFaceIndices.length; i < l; i++) {
      const j = boundFaceIndices[i];
      faceVertices.push(graph.faceVertices[j]);
      faceHalfEdges.push(graph.faceHalfEdges[j]);
    }

    return {
      faceVertices,
      faceHalfEdges,
    };
  }

  // build exterior faces by tracing half edges

  const { vertexHalfEdges, halfEdgeParentFace, halfEdges, halfEdgeVertexIndex } = graph;

  const numHalfEdges = halfEdges.length / 2;

  // for fast lookup
  const isExcludedFace: { [faceIdx: number]: boolean } = {};
  for (const f of excludedFaceIndices) {
    isExcludedFace[f] = true;
  }

  const isBoundingFace: { [faceIdx: number]: boolean } = {};
  for (const f of boundFaceIndices) {
    isBoundingFace[f] = true;
  }

  const visited = new Array<boolean>(numHalfEdges);
  visited.fill(false);

  function halfEdgeIsPartOfTraceBoundary(halfEdge: number) {
    const oppHalfEdge = planarGraphAdjacentHalfEdge(halfEdge);

    const face = halfEdgeParentFace[halfEdge];
    const oppFace = halfEdgeParentFace[oppHalfEdge];

    return (isExcludedFace[face] || isBoundingFace[face]) && !isExcludedFace[oppFace] && !isBoundingFace[oppFace];
  }

  // this function traverses the half edges of the planar graph, generating the loops

  function traceHalfEdges(startHalfEdge: number) {
    let curHalfEdge = startHalfEdge;

    const curFaceVertices: number[] = [];
    const curFaceHalfEdges: number[] = [];

    // trace the boundary of the face
    while (!visited[curHalfEdge]) {
      visited[curHalfEdge] = true;

      const curVertex = halfEdges[curHalfEdge * 2];
      const nxtVertex = halfEdges[curHalfEdge * 2 + 1];

      const nxtVertexHalfEdges = vertexHalfEdges[nxtVertex];

      // find index of half edge from nxtVertex returning to curVertex
      const oppHalfEdge = planarGraphAdjacentHalfEdge(curHalfEdge);
      const halfEdgeLocal = halfEdgeVertexIndex[oppHalfEdge];

      if (!(halfEdgeLocal !== -1)) {
        console.assert(false);
      }

      const nxtCount = nxtVertexHalfEdges.length;

      let newHalfEdge = -1;

      // find next half edge
      for (let i = 1; i < nxtCount; i++) {
        // traverse in CCW order
        const candHalfEdgeLocal = (halfEdgeLocal + i) % nxtCount;
        const candHalfEdge = nxtVertexHalfEdges[candHalfEdgeLocal];

        if (halfEdgeIsPartOfTraceBoundary(candHalfEdge)) {
          newHalfEdge = candHalfEdge;
          break;
        }
      }

      if (!(newHalfEdge !== -1)) {
        console.assert(false);
      }

      curFaceVertices.push(curVertex);
      curFaceHalfEdges.push(curHalfEdge);

      curHalfEdge = newHalfEdge;
    }

    if (curFaceHalfEdges.length >= 3) {
      faceVertices.push(curFaceVertices);
      faceHalfEdges.push(curFaceHalfEdges);
    }
  }

  for (let halfEdge = 0; halfEdge < numHalfEdges; halfEdge++) {
    if (visited[halfEdge]) {
      continue;
    }

    if (halfEdgeIsPartOfTraceBoundary(halfEdge)) {
      traceHalfEdges(halfEdge);
    }
  }

  return {
    faceVertices,
    faceHalfEdges,
  };
}

export function planarGraphTraceConnectedFaces(
  graph: PlanarGraph,
  connectedFacePairs: number[], // [0,1, 2,3] indicates face 0 is connected to face 1, face 2 to face 3, etc
): PlanarGraphLoops {
  // we'll store the result in these data structures
  const faceVertices = new Array<number[]>();
  const faceHalfEdges = new Array<number[]>();

  const { vertexHalfEdges, halfEdgeParentFace, halfEdges, halfEdgeVertexIndex } = graph;
  const numHalfEdges = halfEdges.length / 2;

  // build data structure to rapidly look up connected face pairs
  const faceHasPair: { [faceIdx: number]: boolean } = {};
  const facesConnected: { [facePairHash: number]: boolean } = {};
  for (let i = 0, l = connectedFacePairs.length; i < l; i += 2) {
    const a = connectedFacePairs[i];
    const b = connectedFacePairs[i + 1];

    faceHasPair[a] = true;
    faceHasPair[b] = true;

    facesConnected[hashInts2(a, b)] = true;
    facesConnected[hashInts2(b, a)] = true;
  }

  const visited = new Array<boolean>(numHalfEdges);
  visited.fill(false);

  // this function traverses the half edges of the planar graph, generating the loops

  function traceHalfEdges(startHalfEdge: number) {
    let curHalfEdge = startHalfEdge;

    const curFaceVertices: number[] = [];
    const curFaceHalfEdges: number[] = [];

    // trace the boundary of the face
    while (!visited[curHalfEdge]) {
      visited[curHalfEdge] = true;

      const curVertex = halfEdges[curHalfEdge * 2];
      const nxtVertex = halfEdges[curHalfEdge * 2 + 1];

      const nxtVertexHalfEdges = vertexHalfEdges[nxtVertex];

      // find index of half edge from nxtVertex returning to curVertex
      const oppHalfEdge = planarGraphAdjacentHalfEdge(curHalfEdge);
      const halfEdgeLocal = halfEdgeVertexIndex[oppHalfEdge];

      if (!(halfEdgeLocal !== -1)) {
        console.assert(false);
      }

      const nxtCount = nxtVertexHalfEdges.length;

      let nextHalfEdge = -1;

      // find next half edge

      const curFace = halfEdgeParentFace[curHalfEdge];

      let lastFace = curFace;

      for (let i = 1; i < nxtCount + 1; i++) {
        // traverse in CW order
        const candHalfEdgeLocal = (halfEdgeLocal - i + nxtCount) % nxtCount;
        const candHalfEdge = nxtVertexHalfEdges[candHalfEdgeLocal];

        const oppCandHalfEdge = planarGraphAdjacentHalfEdge(candHalfEdge);
        const oppCandFace = halfEdgeParentFace[oppCandHalfEdge];

        if (facesConnected[hashInts2(oppCandFace, lastFace)]) {
          lastFace = oppCandFace;
          continue;
        }

        nextHalfEdge = candHalfEdge;
        break;
      }

      if (!(nextHalfEdge !== -1)) {
        console.assert(false);
      }

      curFaceVertices.push(curVertex);
      curFaceHalfEdges.push(curHalfEdge);

      curHalfEdge = nextHalfEdge;
    }

    faceVertices.push(curFaceVertices);
    faceHalfEdges.push(curFaceHalfEdges);
  }

  for (let i = 0; i < numHalfEdges; i++) {
    const face = halfEdgeParentFace[i];
    const oppHalfEdge = planarGraphAdjacentHalfEdge(i);
    const oppFace = halfEdgeParentFace[oppHalfEdge];

    if (visited[i]) {
      continue;
    }

    if (faceHasPair[face] && !facesConnected[hashInts2(face, oppFace)]) {
      traceHalfEdges(i);
    }
  }

  return {
    faceVertices,
    faceHalfEdges,
  };
}

export function planarGraphMakeFacePoleOfInaccessibilityPositions(
  planarGraph: PlanarGraph,
): PlanarGraphFacePoleOfInaccessibilityPositions {
  const facePositions: Vec2[] = new Array<Vec2>(planarGraph.faceHalfEdges.length);
  const { faceHalfEdges, faceVertices } = planarGraph;
  for (const faceIndex of Object.keys(faceHalfEdges)) {
    const faceOutline = planarGraphVertexStripToPolyLine2(planarGraph, faceVertices[faceIndex], false);
    const poleOfInaccessibility = poleOfInaccessibilityPolyLine2(faceOutline);

    if (poleOfInaccessibility) {
      facePositions[faceIndex] = poleOfInaccessibility;
    }
  }
  return { facePositions };
}

export function planarGraphPrevHalfEdgeInLoop(halfEdges: number[], halfEdge: number): number {
  for (let i = 0, l = halfEdges.length; i < l; i++) {
    if (halfEdges[i] === halfEdge) {
      return halfEdges[(i + l - 1) % l];
    }
  }

  return -1;
}

export function planarGraphNextHalfEdgeInLoop(halfEdges: number[], halfEdge: number): number {
  for (let i = 0, l = halfEdges.length; i < l; i++) {
    if (halfEdges[i] === halfEdge) {
      return halfEdges[(i + 1) % l];
    }
  }

  return -1;
}
